import { useState } from "react";
import { CompareSlider } from "@/components/CompareSlider";
import { fileUrl } from "@/lib/api";
import { Lock, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/i18n";

const v = (job) => ({ v: job.unlocked ? 1 : 0 });

const LockedNote = () => {
  const { t } = useLang();
  return (
    <div data-testid="locked-preview-note" className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
      <p className="text-sm text-amber-900"><span className="font-semibold">{t("dl.lockedTitle")}.</span> {t("dl.lockedText")}</p>
    </div>
  );
};

const UpscaleView = ({ job }) => {
  const { t } = useLang();
  return <CompareSlider before={fileUrl(job.job_id, "original")} after={fileUrl(job.job_id, "preview", v(job))} beforeLabel={t("result.original")} afterLabel={`HD ${job.params?.scale || 4}x`} />;
};

const BgView = ({ job }) => {
  const { t } = useLang();
  return (
    <div>
      <CompareSlider testId="bg-compare-slider" className="checkerboard" pixelate={false} before={fileUrl(job.job_id, "original")} after={fileUrl(job.job_id, "preview", v(job))}
        beforeLabel={t("result.original")} afterLabel={t("result.noBg")} />
      <p className="mt-3 font-mono text-xs uppercase tracking-wider text-slate-500" data-testid="bg-compare-hint">{t("result.dragHint")}</p>
    </div>
  );
};

const ENGINE_ORDER = ["recraft", "precise"];

const VectorCard = ({ job, engine, selected, onSelect, onZoom }) => {
  const { t } = useLang();
  const src = fileUrl(job.job_id, "preview", { engine, ...v(job) });
  return (
    <div data-testid={`vector-engine-${engine}`} onClick={() => onSelect(engine)}
      className={`cursor-pointer rounded-xl border-2 bg-white p-4 transition-colors ${selected ? "border-[#E40046]" : "border-slate-200 hover:border-slate-400"}`}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">{t(`vec.${engine}`)}</p>
          <p className="text-[11px] leading-snug text-slate-500">{t(`vec.${engine}Hint`)}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${selected ? "bg-[#E40046] text-white" : "bg-slate-100 text-slate-500"}`}>
          {selected ? t("vec.selected") : t("vec.choose")}
        </span>
      </div>
      <div className="grid-paper flex h-[340px] items-center justify-center rounded-lg">
        <img data-testid={`vector-preview-${engine}`} src={src} alt={t(`vec.${engine}`)} className="max-h-full max-w-full object-contain" />
      </div>
      <button data-testid={`vector-zoom-${engine}`} onClick={(e) => { e.stopPropagation(); onZoom(src); }}
        className="mt-3 flex items-center gap-1 font-mono text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900">
        <ZoomIn className="h-3.5 w-3.5" />{t("vec.zoom")}
      </button>
    </div>
  );
};

const VectorView = ({ job, engine, setEngine }) => {
  const { t } = useLang();
  const [zoom, setZoom] = useState(null);
  const engines = ENGINE_ORDER.filter((e) => (job.engines?.length ? job.engines : ["recraft"]).includes(e));
  return (
    <div>
      <p className="mb-3 text-sm text-slate-600" data-testid="vector-compare-hint">{engines.length > 1 ? t("vec.compare") : t("vec.single")}</p>
      <div className={`grid gap-4 ${engines.length > 1 ? "sm:grid-cols-2" : ""}`}>
        {engines.map((e) => <VectorCard key={e} job={job} engine={e} selected={engine === e} onSelect={setEngine} onZoom={setZoom} />)}
      </div>
      <div className="mt-4 flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3">
        <div className="grid-paper flex h-20 w-32 shrink-0 items-center justify-center rounded-lg">
          <img data-testid="vector-original-image" src={fileUrl(job.job_id, "original")} alt={t("result.bitmap")} className="max-h-full max-w-full object-contain" />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{t("result.bitmap")}</p>
      </div>
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-6xl" data-testid="vector-zoom-dialog">
          <DialogTitle className="sr-only">{t("vec.zoom")}</DialogTitle>
          <div className="grid-paper flex max-h-[80vh] items-center justify-center overflow-auto rounded-lg">
            {zoom && <img src={zoom} alt="" className="w-full object-contain" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PdfView = ({ job, page, setPage }) => {
  const { t } = useLang();
  return (
    <div>
      <div className="flex min-h-[420px] items-center justify-center rounded-xl bg-slate-100 p-6">
        <img data-testid="pdf-page-preview" src={fileUrl(job.job_id, "original", { page, ...v(job) })} alt={t("result.page", { n: page + 1 })} className="max-h-[560px] bg-white shadow-xl" />
      </div>
      <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
        {Array.from({ length: job.pages }).map((_, i) => (
          <button key={i} data-testid={`pdf-thumb-${i}`} onClick={() => setPage(i)} className={`shrink-0 rounded-md border-2 p-1 transition-colors ${i === page ? "border-[#00A3E0]" : "border-transparent hover:border-slate-300"}`}>
            <img src={fileUrl(job.job_id, "original", { page: i, ...v(job) })} alt="" loading="lazy" className="h-24 bg-white" />
            <span className="mt-1 block font-mono text-[10px] text-slate-500">{i + 1}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const VIEWS = { upscale: UpscaleView, remove_bg: BgView, vectorize: VectorView, pdf: PdfView };

export const ResultView = ({ job, tool, page, setPage, engine, setEngine }) => {
  const View = VIEWS[tool.key];
  return (
    <div>
      {!job.unlocked && <LockedNote />}
      <View job={job} page={page} setPage={setPage} engine={engine} setEngine={setEngine} />
    </div>
  );
};
